import type { Chain } from "@opensea/app-kit"
import { DEFAULT_CHAINS } from "@opensea/app-kit"
import type { Transport, Chain as ViemChain } from "viem"
import { http } from "viem"
import type { CreateConnectorFn, Storage } from "wagmi"
import { fallback, createConfig as wagmiCreateConfig } from "wagmi"
import { coinbaseWallet, metaMask } from "wagmi/connectors"
import { OPENSEA_LOGO_URL } from "../assets"
import { abstractConnector } from "./abstractConnector"
import { getChainRpcUrls, toViemChain } from "./chainUtils"
import { glyphConnector } from "./glyphConnector"
import type { Environment } from "./osEmbeddedWalletConnector"
import { osEmbeddedWalletConnector } from "./osEmbeddedWalletConnector"
import { walletConnectConnector } from "./walletConnectConnector"

export type ConfigParams = {
  supportInjectedWallets?: boolean
  chains?: Chain[]
  connectors?: CreateConnectorFn[]
  environment?: Environment
  abstractEnabled?: boolean
  glyphEnabled?: boolean
  privyClientId?: string
  walletConnectId?: string
  storage?: Storage
  transports?: Record<number, Transport>
  ssr?: boolean
}

export const getDefaultConnectors = (config: ConfigParams) => {
  const connectors: CreateConnectorFn[] = [
    osEmbeddedWalletConnector({
      clientId: config.privyClientId,
      environment: config.environment,
      debug: true,
    }),
    metaMask({
      dappMetadata: {
        name: "OpenSea",
      },
    }),
    coinbaseWallet({
      appName: "OpenSea",
      appLogoUrl: OPENSEA_LOGO_URL,
    }),
  ]

  connectors.push(
    walletConnectConnector({
      projectId: config.walletConnectId ?? "00cdc4f59fdd37f4a845e1b8bee6ecd1",
    }),
  )

  if (config.abstractEnabled) {
    connectors.push(abstractConnector())
  }

  if (config.glyphEnabled) {
    connectors.push(glyphConnector())
  }

  return connectors
}

export const getDefaultTransports = (chains: Chain[]) => {
  return Object.fromEntries(
    chains
      .filter(c => c.arch === "EVM")
      .map(c => {
        const rpcUrls = getChainRpcUrls(c)
        return [c.networkId, fallback(rpcUrls.map(rpcUrl => http(rpcUrl)))]
      }),
  )
}

export function createConfig(config: ConfigParams = {}) {
  const walletChains =
    config.chains && config.chains.length > 0 ? config.chains : DEFAULT_CHAINS
  const connectors = config.connectors ?? getDefaultConnectors(config)
  const transports = config.transports ?? getDefaultTransports(walletChains)

  // Convert wallet chains to Viem chains for wagmi
  const viemChains = walletChains.map(toViemChain) as [
    ViemChain,
    ...ViemChain[],
  ]

  return wagmiCreateConfig({
    chains: viemChains,
    transports,
    connectors,
    multiInjectedProviderDiscovery: config.supportInjectedWallets,
    ssr: config.ssr,
    storage: config.storage,
  })
}
